import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, TrendingUp, Briefcase, FileText, Bell, Calendar,
  LogOut, User, ChevronRight, ArrowUpRight, ArrowDownRight, Clock,
  DollarSign, Target, PieChart, Loader2, Menu, X, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestorDashboard = () => {
  const { user, logout, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [portfolio, setPortfolio] = useState({ investments: [], summary: {} });
  const [deals, setDeals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get user from location state or auth context
  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (!currentUser && !authLoading) {
      checkAuth();
    }
  }, [currentUser, authLoading, checkAuth]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [portfolioRes, dealsRes, docsRes, notifsRes, meetingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/investor/portfolio`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/investor/deals`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/notifications`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/meetings`, { withCredentials: true })
      ]);
      
      setPortfolio(portfolioRes.data);
      setDeals(dealsRes.data);
      setDocuments(docsRes.data);
      setNotifications(notifsRes.data);
      setMeetings(meetingsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 401) {
        navigate('/portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(
        `${API_URL}/api/portal/notifications/${notificationId}/read`,
        {},
        { withCredentials: true }
      );
      setNotifications(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => 
          n.notification_id === notificationId ? { ...n, read: true } : n
        ),
        unread_count: prev.unread_count - 1
      }));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  if (authLoading || (!currentUser && !loading)) {
    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#60B5AF] animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'portfolio', label: 'Portfolio', icon: PieChart },
    { id: 'deals', label: 'Deal Pipeline', icon: Briefcase },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: notifications.unread_count },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#1A3A4A] transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <a href="/" className="font-heading text-2xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
            <p className="font-body text-white/40 text-xs mt-1">Investor Portal</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm transition-all ${
                  activeTab === item.id 
                    ? 'bg-[#60B5AF] text-white' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              {currentUser?.picture ? (
                <img src={currentUser.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#60B5AF]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#60B5AF]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-white text-sm truncate">{currentUser?.name}</p>
                <p className="font-body text-white/40 text-xs truncate">{currentUser?.email}</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#1A3A4A]/10 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6 text-[#1A3A4A]" />
        </button>
        <span className="font-heading font-bold text-[#1A3A4A]">HÜBNER<span className="text-[#60B5AF]">.</span></span>
        <div className="w-10" />
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#60B5AF] animate-spin" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">
                    Welcome back, {currentUser?.name?.split(' ')[0]}
                  </h1>
                  <p className="font-body text-[#1A3A4A]/60">Here's your investment overview</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard 
                    title="Total Invested" 
                    value={`€${(portfolio.summary.total_invested || 0).toLocaleString()}`}
                    icon={DollarSign}
                    trend="up"
                  />
                  <StatCard 
                    title="Total Returns" 
                    value={`€${(portfolio.summary.total_returns || 0).toLocaleString()}`}
                    icon={TrendingUp}
                    trend={portfolio.summary.total_returns > 0 ? 'up' : 'neutral'}
                  />
                  <StatCard 
                    title="Active Investments" 
                    value={portfolio.summary.active_investments || 0}
                    icon={Briefcase}
                  />
                  <StatCard 
                    title="Open Deals" 
                    value={deals.filter(d => d.status === 'open').length}
                    icon={Target}
                  />
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Latest Deals */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-[#1A3A4A]">New Opportunities</h3>
                      <button 
                        onClick={() => setActiveTab('deals')}
                        className="text-sm text-[#60B5AF] hover:underline font-body flex items-center gap-1"
                      >
                        View all <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {deals.slice(0, 3).map((deal) => (
                      <div key={deal.deal_id} className="flex items-center justify-between py-3 border-b border-[#1A3A4A]/5 last:border-0">
                        <div>
                          <p className="font-body font-medium text-[#1A3A4A]">{deal.title}</p>
                          <p className="font-body text-xs text-[#1A3A4A]/60">{deal.sector} • {deal.stage}</p>
                        </div>
                        <span className="text-sm font-body text-[#60B5AF]">€{deal.minimum_ticket?.toLocaleString()}+</span>
                      </div>
                    ))}
                    {deals.length === 0 && (
                      <p className="font-body text-[#1A3A4A]/40 text-center py-8">No open deals at the moment</p>
                    )}
                  </div>

                  {/* Notifications */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-[#1A3A4A]">Recent Notifications</h3>
                      <button 
                        onClick={() => setActiveTab('notifications')}
                        className="text-sm text-[#60B5AF] hover:underline font-body flex items-center gap-1"
                      >
                        View all <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {notifications.notifications.slice(0, 3).map((notif) => (
                      <div 
                        key={notif.notification_id} 
                        className={`flex items-start gap-3 py-3 border-b border-[#1A3A4A]/5 last:border-0 ${!notif.read ? 'bg-[#60B5AF]/5 -mx-2 px-2 rounded-lg' : ''}`}
                      >
                        <div className={`w-2 h-2 rounded-full mt-2 ${!notif.read ? 'bg-[#60B5AF]' : 'bg-[#1A3A4A]/20'}`} />
                        <div>
                          <p className="font-body font-medium text-sm text-[#1A3A4A]">{notif.title}</p>
                          <p className="font-body text-xs text-[#1A3A4A]/60">{notif.message}</p>
                        </div>
                      </div>
                    ))}
                    {notifications.notifications.length === 0 && (
                      <p className="font-body text-[#1A3A4A]/40 text-center py-8">No notifications</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Portfolio Tab */}
            {activeTab === 'portfolio' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Investment Portfolio</h1>
                
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F8FAFB]">
                        <tr>
                          <th className="text-left font-body font-medium text-[#1A3A4A]/60 text-sm px-6 py-4">Company</th>
                          <th className="text-left font-body font-medium text-[#1A3A4A]/60 text-sm px-6 py-4">Amount</th>
                          <th className="text-left font-body font-medium text-[#1A3A4A]/60 text-sm px-6 py-4">Date</th>
                          <th className="text-left font-body font-medium text-[#1A3A4A]/60 text-sm px-6 py-4">Status</th>
                          <th className="text-left font-body font-medium text-[#1A3A4A]/60 text-sm px-6 py-4">Returns</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolio.investments.map((inv) => (
                          <tr key={inv.investment_id} className="border-t border-[#1A3A4A]/5">
                            <td className="px-6 py-4 font-body font-medium text-[#1A3A4A]">{inv.company_name}</td>
                            <td className="px-6 py-4 font-body text-[#1A3A4A]">{inv.currency} {inv.amount?.toLocaleString()}</td>
                            <td className="px-6 py-4 font-body text-[#1A3A4A]/60">{inv.date}</td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${
                                inv.status === 'active' ? 'bg-green-100 text-green-700' :
                                inv.status === 'exited' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 font-body">
                              {inv.returns_percentage !== null ? (
                                <span className={inv.returns_percentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {inv.returns_percentage >= 0 ? '+' : ''}{inv.returns_percentage}%
                                </span>
                              ) : (
                                <span className="text-[#1A3A4A]/40">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {portfolio.investments.length === 0 && (
                    <div className="text-center py-12">
                      <PieChart className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No investments yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Deals Tab */}
            {activeTab === 'deals' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Deal Pipeline</h1>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {deals.map((deal) => (
                    <div key={deal.deal_id} className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className={`px-2 py-1 rounded text-xs font-body font-medium ${
                            deal.status === 'open' ? 'bg-green-100 text-green-700' :
                            deal.status === 'closing_soon' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {deal.status === 'closing_soon' ? 'Closing Soon' : deal.status}
                          </span>
                        </div>
                        <span className="font-body text-xs text-[#1A3A4A]/40">{deal.sector}</span>
                      </div>
                      <h3 className="font-heading font-semibold text-[#1A3A4A] mb-1">{deal.title}</h3>
                      <p className="font-body text-sm text-[#1A3A4A]/60 mb-4">{deal.company_name}</p>
                      <p className="font-body text-sm text-[#1A3A4A]/70 mb-4 line-clamp-2">{deal.description}</p>
                      <div className="flex justify-between items-center pt-4 border-t border-[#1A3A4A]/5">
                        <div>
                          <p className="font-body text-xs text-[#1A3A4A]/40">Min. Ticket</p>
                          <p className="font-body font-medium text-[#1A3A4A]">€{deal.minimum_ticket?.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-body text-xs text-[#1A3A4A]/40">Target Raise</p>
                          <p className="font-body font-medium text-[#1A3A4A]">€{deal.target_raise?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {deals.length === 0 && (
                  <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <Briefcase className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                    <p className="font-body text-[#1A3A4A]/40">No open deals at the moment</p>
                  </div>
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Documents</h1>
                
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-4 border-b border-[#1A3A4A]/5 last:border-0 hover:bg-[#F8FAFB]">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#60B5AF]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#60B5AF]" />
                        </div>
                        <div>
                          <p className="font-body font-medium text-[#1A3A4A]">{doc.filename}</p>
                          <p className="font-body text-xs text-[#1A3A4A]/60">
                            {doc.category} • {doc.uploaded_at?.split('T')[0]}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-[#60B5AF]">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {documents.length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No documents yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Upcoming Meetings</h1>
                
                <div className="space-y-4">
                  {meetings.map((meeting) => (
                    <div key={meeting.meeting_id} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-6">
                      <div className="text-center min-w-[60px]">
                        <p className="font-heading text-2xl font-bold text-[#1A3A4A]">
                          {new Date(meeting.date).getDate()}
                        </p>
                        <p className="font-body text-xs text-[#1A3A4A]/60 uppercase">
                          {new Date(meeting.date).toLocaleDateString('en', { month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-body font-medium text-[#1A3A4A]">{meeting.title}</h3>
                        <p className="font-body text-sm text-[#1A3A4A]/60">
                          {meeting.time} • {meeting.duration_minutes} min
                        </p>
                        {meeting.description && (
                          <p className="font-body text-sm text-[#1A3A4A]/40 mt-1">{meeting.description}</p>
                        )}
                      </div>
                      {meeting.meeting_link && (
                        <a 
                          href={meeting.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-[#60B5AF] text-white rounded-full font-body text-sm hover:bg-[#60B5AF]/90"
                        >
                          Join
                        </a>
                      )}
                    </div>
                  ))}
                  {meetings.length === 0 && (
                    <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                      <Calendar className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No upcoming meetings</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Notifications</h1>
                
                <div className="bg-white rounded-2xl shadow-sm">
                  {notifications.notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      onClick={() => !notif.read && markNotificationRead(notif.notification_id)}
                      className={`flex items-start gap-4 p-4 border-b border-[#1A3A4A]/5 last:border-0 cursor-pointer hover:bg-[#F8FAFB] ${
                        !notif.read ? 'bg-[#60B5AF]/5' : ''
                      }`}
                    >
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${!notif.read ? 'bg-[#60B5AF]' : 'bg-[#1A3A4A]/20'}`} />
                      <div className="flex-1">
                        <p className="font-body font-medium text-[#1A3A4A]">{notif.title}</p>
                        <p className="font-body text-sm text-[#1A3A4A]/60">{notif.message}</p>
                        <p className="font-body text-xs text-[#1A3A4A]/40 mt-1">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {notifications.notifications.length === 0 && (
                    <div className="text-center py-12">
                      <Bell className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#60B5AF]/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#60B5AF]" />
      </div>
      {trend && (
        <span className={`flex items-center text-xs font-body ${
          trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-[#1A3A4A]/40'
        }`}>
          {trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
          {trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
        </span>
      )}
    </div>
    <p className="font-body text-sm text-[#1A3A4A]/60">{title}</p>
    <p className="font-heading text-2xl font-bold text-[#1A3A4A]">{value}</p>
  </div>
);

export default InvestorDashboard;
